import React, { useRef, useState } from "react";
import {
  StyledTypography,
  StyledBox,
  StyledLink,
  StyledCheckbox,
  StyledFormControlLabel,
  StyledSwatch,
} from "../../../styled-mui";
import { Box, FormGroup, useMediaQuery, useTheme } from "@mui/material";
import { TYPE_CARD } from "../../../constants/catalog";
import { ProductAvis } from "../product-avis/ProductAvis";
import { ImageLayout } from "../image";
import { dimensions } from "../../../themes/variables";
import { PriceDisplay } from "../formatted-price-display/formatted-price-display";
import storeUtil from "../../../utils/storeUtil";
import { SvgCheckBoxOutlineBlankIcon } from "../../svg/checkBoxOutlineBlankIcon";
import { SvgCheckThick } from "../../svg/checkThick";
import { StyledProductCard } from "./styled-productCard";
import { StyledProductImageLabel, StyledProductImageTag } from "../product-image/styled-productImage";
import AsyncCall from "../../../_foundation/gtm/async.service";
import { CLICK } from "../../../_foundation/constants/gtm";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import getDisplayName from "react-display-name";
import kebabCase from "lodash-es/kebabCase";
import { GetCategoriesSelector, GetCurrentCategorySelector } from "../../../redux/selectors/category";
import { useSite } from "../../../_foundation/hooks/useSite";
import { getProductPriceData } from "../../../utils/priceUtil";
import Chip from "@mui/material/Chip";
import ColorSwatches from "../color-swatches/ColorSwatches";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface ProductCardProps {
  product: any;
  type?: TYPE_CARD;
  defaultProduct?: boolean;
  onSelect?: (action: string, product: any) => void;
  addToCart?: (partNumber: string) => void;
  categoryId?: string;
  [key: string]: any;
  showAttribute?: boolean;
  attributes?: any;
  isRecommanded?: any;
  project?: boolean;
  productPosition?: number;
  plpCard?: boolean; // Used to identify if the card is used in PLP
  changeImageOnHover?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onSelect,
  attributes,
  type = TYPE_CARD.RECOMMENDATION,
  defaultProduct = false,
  showAttribute = false,
  isRecommanded,
  project,
  productPosition = 0,
  plpCard = false, // Used to identify if the card is used in PLP
  changeImageOnHover = true,
}) => {
  const swatches: any[] = [];
  const navigate = useNavigate();
  const productAttributes: any = product?.attributes ? product?.attributes : [];
  const categoriesRedux = useSelector(GetCategoriesSelector);
  const currentCategory = useSelector(GetCurrentCategorySelector);
  const location: any = useLocation();
  const componentName: any = getDisplayName(ProductCard);
  const { pathname } = location;
  const pageName: string = pathname === "/" ? "homepage" : pathname.replaceAll("/", "");
  const { mySite } = useSite();
  const itemListId = componentName + "_" + kebabCase(pageName);
  const itemListName = componentName + " " + pageName;
  const badges: any[] = storeUtil.getBadgesFpcProduct(product, mySite);
  const [showFirstImage, setShowFirstImage] = useState<boolean>(true);
  const projectFlow = location.state?.projectFlow;
  const trackingIndex = productPosition + 1;

  const isSurMesure = product?.UserData?.[0]?.["custom.fields.*"]?.[0]?.catent_field4?.raw === "SUR_MESURE";
  const productOptions = product?.attributes?.filter((p) => p?.usage === "Defining");
  const productOptionsCount = productOptions.reduce((total, attr) => {
    const values = attr?.values?.[0]?.value;
    return total + (Array.isArray(values) ? values.length : 0);
  }, 0);

  const displayTags = storeUtil.getTagsProduct(product) || [];

  const { promoPrice: promotionalOfferPrice, remisedPriceDetails } = getProductPriceData(product);
  const IS_REMISED_PRICE_VALID = remisedPriceDetails?.valid || false;
  const REMISED_OLD_PRICE_END_DATE = remisedPriceDetails?.OldPriceEnd || null;
  const REMISED_OLD_PRICE = remisedPriceDetails?.oldPrice || null;

  const currency = "EUR";
  const IntlPrice = Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const isFPC = product?.partNumber?.startsWith('FPC');

  let pdpImages;

  if(isFPC)
  {
    pdpImages = product?.images?.filter((p)=>p?.catentry_id && p?.thumbnailRaw)
  } else {
    pdpImages = product?.images?.filter(
      (p) => !("catentry_id" in p)
    );
  }

  const carouselImages = pdpImages
    ?.map((att: any) => att.thumbnailRaw) || [product?.thumbnailRaw];

  // const carouselImages = ["/mobileList/824/294/201824294.jpg","/mobileList/824/264/201824264.jpg","/mobileList/824/278/201824278.jpg"]

  // Get first image
  const firstImageSrc = product?.thumbnailRaw;

  // Get MOUSE_HOVER_ON_IMAGE_USAGE with safety check
  const MOUSE_HOVER_ON_IMAGE_USAGE = mySite?.storeCfg?.userData?.MOUSE_HOVER_ON_IMAGE_USAGE
    ? mySite.storeCfg.userData.MOUSE_HOVER_ON_IMAGE_USAGE.split(",").map((usage) => usage.trim())
    : [];

  // Get filtered images
  const filteredImages =
    product?.attachments?.filter((image) => MOUSE_HOVER_ON_IMAGE_USAGE.includes(image.usage)) || [];

  // Fixed getSecondImageSrc function - use useMemo or move calculation outside
  const getSecondImageSrc = () => {
    if (!filteredImages || filteredImages.length === 0) return null;

    const secondImage =
      filteredImages.find((img) => img.usage === "GRIDIMAGES_AMBIANCE") ||
      filteredImages.find((img) => img.usage === "GRIDIMAGES_DETOURE") ||
      filteredImages[0];

    return secondImage?.attachmentAssetPathRaw || null;
  };

  // Calculate second image source
  const secondImageSrc = getSecondImageSrc();

  const handlerChange = (event) => {
    onSelect && onSelect(event.target.checked ? "add" : "remove", product);
  };

  const finitions = storeUtil.getFinitionValuesAttributes(productAttributes);
  const colors = storeUtil.getColorAttributes(productAttributes);

  // Get the image filename to avoid showing the same image on hover
  const getFileName = (path: string) => path?.split("/").pop();

  colors?.values &&
    colors?.values?.forEach((attributeValue: any, index2: number) => {
      if (
        attributeValue.image1path &&
        Array.isArray(attributeValue.image1path) &&
        attributeValue.image1path.length > 0
      ) {
        attributeValue.image1.forEach((imagePath: any, index3: number) => {
          swatches.push(
            <StyledSwatch
              className="rounded-small"
              style={{
                backgroundImage: `url("${storeUtil.getUrlPicture(imagePath)}")`,
              }}
              key={`${attributeValue.id[index3]}_${index2}_${index3}`}
              title={attributeValue.value[index3]}
              data-testid={`product-card-layout-${attributeValue.id[index3].toLowerCase()}-swatch`}
            />
          );
        });
      } else if (attributeValue.image1path?.length > 0) {
        swatches.push(
          <StyledSwatch
            className="rounded-small"
            style={{
              backgroundImage: `url("${attributeValue.image1path}")`,
            }}
            key={`${attributeValue.id}_${index2}`}
            title={attributeValue.value}
            data-testid={`product-card-layout-${attributeValue.id.toLowerCase()}-swatch`}
          />
        );
      }
    });

  const cardClickHandler = () => {
    AsyncCall.sendClickProduct(CLICK.SELECT_ITEM, {
      topCategoriesList: categoriesRedux,
      product: product,
      label: `card-${type}-${product?.name}`,
      index: trackingIndex,
      item_list_id: itemListId,
      item_list_name: itemListName,
      itemPrice: promotionalOfferPrice || product?.price?.[0]?.value,
    });
    if (isRecommanded?.recommendationId && isRecommanded?.widgetId) {
      AsyncCall.sendClick(CLICK.PRODUCT_RECOMMANDED, {
        sourceID: isRecommanded?.recommendationId,
        widgetID: isRecommanded?.widgetId,
        product_fpc: product.partNumber,
      });
    }
  };

  function getImageCardWidth() {
    if (type === TYPE_CARD.LIST) {
      if (isMobile) {
        return dimensions.cardImage.list.xs;
      } else return dimensions.cardImage.list.sm;
    }
    if (type === TYPE_CARD.RECOMMENDATION) {
      if (isMobile) {
        return "100%";
      } else return dimensions.cardImage.small;
    } else return "100%";
  }

  const formatAttributes = (attributes) => {
    const attributeString = Array.isArray(attributes) ? attributes.join("") : attributes;
    return attributeString.replace(/,/g, ", ");
  };

  // Fixed condition to check if hover should work
  const checkImageOnHover = Boolean(
    changeImageOnHover && firstImageSrc && secondImageSrc && getFileName(firstImageSrc) !== getFileName(secondImageSrc)
  );

  const onMouseEnterHandler = () => {
    if (checkImageOnHover) {
      setShowFirstImage(false);
    }
  };

  const onMouseLeaveHandler = () => {
    if (checkImageOnHover) {
      setShowFirstImage(true);
    }
  };
  // Current image source to display
  const currentImageSrc = showFirstImage ? firstImageSrc : secondImageSrc;

  const getAmbianceImageSrc = () => {
    if (!filteredImages || filteredImages.length === 0) return null;
    const ambianceImage = filteredImages.find((img) => img.usage === "GRIDIMAGES_AMBIANCE");
    return ambianceImage?.attachmentAssetPathRaw || null;
  };

  const ambinaceImageSrc = getAmbianceImageSrc();

  // const carouselImages: string[] = [];
  // if (firstImageSrc) {
  //   carouselImages.push(firstImageSrc);

  //   if (plpCard && ambinaceImageSrc && getFileName(firstImageSrc) !== getFileName(ambinaceImageSrc)) {
  //     carouselImages.push(ambinaceImageSrc);
  //   }
  // }

  const scrollToIndex = (index: number) => {
    if (!carouselRef.current) return;

    const width = carouselRef.current.clientWidth;
    carouselRef.current.scrollTo({
      left: index * width,
      behavior: "smooth",
    });

    setActiveImageIndex(index);
  };

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeImageIndex > 0) {
      scrollToIndex(activeImageIndex - 1);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeImageIndex < carouselImages.length - 1) {
      scrollToIndex(activeImageIndex + 1);
    }
  };

  const enableCarousel = true;

  const promoPercentage =
    "promoPercentage.numeric" in product && Number(product["promoPercentage.numeric"]) > 0
      ? Number(product["promoPercentage.numeric"])
      : null;

  const PriceDropRibbon = () => {
    if (!IS_REMISED_PRICE_VALID || (!REMISED_OLD_PRICE && !REMISED_OLD_PRICE_END_DATE)) {
      return null;
    }

    return <StyledProductImageTag className="list price-drop">Prix en baisse</StyledProductImageTag>;
  };

  const shouldShowPriceDropRibbon =
    !promoPercentage && IS_REMISED_PRICE_VALID && (REMISED_OLD_PRICE || REMISED_OLD_PRICE_END_DATE);

  return (
    <StyledProductCard>
      <StyledBox className="product-card" sx={{ position: "relative" }}>
        <StyledBox
          onClick={(e) => {
            e.preventDefault();
            // navigate
            navigate(
              !mySite.storeCfg.userData.SEO_FE_DISABLE ||
                (mySite.storeCfg.userData.SEO_FE_DISABLE && mySite.storeCfg.userData.SEO_FE_DISABLE !== "true")
                ? `/produits${product?.seo?.href}`
                : product?.seo?.href
            );

            cardClickHandler();
          }}
          style={{ cursor: "pointer" }}>
          <StyledBox p={{ xs: 0, sm: type === TYPE_CARD.LIST && 5 }} style={{ position: "relative" }}>
            <>
              {/* SHOW PROMO PERCENTAGE AS BADGE IF IT'S PRESENT */}
              {/* {promoPercentage && (
                <StyledProductImageTag className={`list ${displayTags[0].className}`}>
                  {`-${promoPercentage} %`}
                </StyledProductImageTag>
              )} */}

              {/* PRICE DROP RIBBON */}
              {shouldShowPriceDropRibbon && <PriceDropRibbon />}

              {/* OTHER BADGE — only if price drop is NOT shown */}
              {!shouldShowPriceDropRibbon && !promoPercentage && displayTags?.length > 0 && (
                <StyledProductImageTag className={`list ${displayTags[0].className}`}>
                  {displayTags[0].description === "Série Limitée" && <AccessTimeIcon fontSize="small" />}
                  {displayTags[0].description}
                </StyledProductImageTag>
              )}

              {/* {badges && badges.length > 0 && (
                <StyledProductImageLabel>
                  {badges.map((badge, index) => (
                    <li key={index}>
                      <ImageLayout
                        src={mySite.assetsPictoUrl + badge.IMAGE1}
                        alt={badge.VALUE}
                        width="40"
                        height="40"
                      />
                    </li>
                  ))}
                </StyledProductImageLabel>
              )} */}
            </>
            <Box
              onMouseEnter={onMouseEnterHandler}
              onMouseLeave={onMouseLeaveHandler}
              style={{ cursor: "pointer", position: "relative" }}
              className="product-image-wrapper">
              {enableCarousel ? (
                <>
                  {/* 🔹 CAROUSEL */}
                  <Box
                    ref={carouselRef}
                    onScroll={(e) => {
                      const scrollLeft = e.currentTarget.scrollLeft;
                      const width = e.currentTarget.clientWidth;
                      const index = Math.round(scrollLeft / width);
                      setActiveImageIndex(index);
                    }}
                    sx={{
                      display: "flex",
                      overflowX: "auto",
                      scrollSnapType: "x mandatory",
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                    }}>
                    {carouselImages.map((imgSrc, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          flex: "0 0 100%",
                          scrollSnapAlign: "start",
                          display: "flex",
                          justifyContent: "center",
                        }}>
                        <ImageLayout
                          src={storeUtil.getUrlPicture(imgSrc)}
                          alt={product.shortDescription}
                          width="100%"
                          height={200}
                          className={`product-card-image ${type === TYPE_CARD.RECOMMENDATION ? "max-height-148" : ""}`}
                          styles={{ objectFit: "contain" }}
                        />
                      </Box>
                    ))}
                  </Box>

                  {/* ⬅️ LEFT ARROW */}
                  {carouselImages.length > 1 && activeImageIndex > 0 && (
                    <ArrowBackIosIcon
                      onClick={handlePrev}
                      className="carousel-arrow left"
                      sx={{ position: "absolute", top: "50%", left: -14, height: "15px" }}
                    />
                  )}

                  {/* ➡️ RIGHT ARROW */}
                  {carouselImages.length > 1 && activeImageIndex < carouselImages.length - 1 && (
                    <ArrowForwardIosIcon
                      onClick={handleNext}
                      className="carousel-arrow right"
                      sx={{ position: "absolute", top: "50%", right: -14, height: "15px" }}
                    />
                  )}

                  {/* 🔹 DOTS (OUTSIDE map) */}
                  {carouselImages.length > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "6px",
                        mt: { xs: 2, sm: 1 }, // ⬅️ more space on mobile
                        pb: { xs: 1, sm: 0 }, // ⬅️ optional breathing space
                      }}>
                      {carouselImages.map((_, index) => (
                        <Box
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            carouselRef.current?.scrollTo({
                              left: index * carouselRef.current.clientWidth,
                              behavior: "smooth",
                            });
                          }}
                          sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            border:
                              index === activeImageIndex ? "1px solid #212121" : "1px solid rgba(33, 33, 33, 0.40)",
                            backgroundColor: index === activeImageIndex ? "#212121" : "#FFFFFF",
                            padding: "4px",
                            cursor: "pointer",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <ImageLayout
                  src={storeUtil.getUrlPicture(currentImageSrc)}
                  alt={product.shortDescription}
                  width={getImageCardWidth()}
                  height={200}
                  className={`product-card-image ${type === TYPE_CARD.RECOMMENDATION ? "max-height-148" : ""}`}
                  priority={(plpCard && trackingIndex === 1) || trackingIndex === 3}
                />
              )}
            </Box>
          </StyledBox>

          <StyledBox
            className="product-card-content"
            p={4}
            pb={15}
            sx={{ position: "relative" }}
            // sx={{ borderBottom: type === TYPE_CARD.RECOMMENDATION ? `${palette.gray.neutralGray300} 1px solid` : "" }}
          >
            <StyledTypography
              variant="subtitle2"
              component="h2"
              className="product-card-title"
              pb={1}
              sx={{ textTransform: "none" }}>
              <StyledLink
                href={
                  !mySite.storeCfg.userData.SEO_FE_DISABLE ||
                  (mySite.storeCfg.userData.SEO_FE_DISABLE && mySite.storeCfg.userData.SEO_FE_DISABLE !== "true")
                    ? `/produits${product?.seo?.href}`
                    : product?.seo?.href
                }
                onClick={cardClickHandler}
                className="product-card-link">
                {mySite?.storeCfg?.userData?.PLP_DISPLAY_SKU_CATEGORIES.includes(currentCategory?.identifier) &&
                !product?.partNumber.includes("FPC")
                  ? storeUtil.getNameProduct(product)
                  : product.name}
              </StyledLink>
            </StyledTypography>
  
            <ProductAvis yellowStarRatings avis={storeUtil.getAvisProduct(product)} className="bottom-padding-4" />

            {showAttribute && (
              <StyledTypography
                variant="body2"
                component="p"
                pb={4}
                sx={{ height: "36px", overflow: "hidden", textOverflow: "ellipsis", marginBottom: "8px" }}>
                {attributes && formatAttributes(storeUtil.getAttributes(attributes))}
                {!attributes && product.attributes && formatAttributes(storeUtil.getAttributesProduct(product))}
              </StyledTypography>
            )}

            {product?.["promoPriceData.numeric"] && product?.["promoPriceData.numeric"] !== "null" && (
              <PriceDisplay
                showNoPriceFoundTxt
                product={product}
                variant="h3"
                hideLeadInTxt={!isSurMesure}
                isPLP={plpCard}
              />
            )}

            {/* {type === TYPE_CARD.LIST && (
              <StyledBox pt={3}> */}
            {/* {product &&
                  product["custom.items.childSkuCount.numeric"] !== undefined &&
                  parseInt(product["custom.items.childSkuCount.numeric"]) > 1 && (
                    <StyledTypography variant="body4" component="p" pb={1}>
                      {`${parseInt(product["custom.items.childSkuCount.numeric"])} déclinaisons`}
                    </StyledTypography>
                  )}
                {finitions?.length > 0 && (
                  <StyledTypography variant="body4" component="p" pb={1}>
                    {`${finitions?.length} ${finitions?.length > 1 ? "finitions" : "finition"}`}
                  </StyledTypography>
                )} */}

            {/* {swatches?.length > 1 && (
                  <StyledTypography
                    variant="body4"
                    component="p"
                    pb={1}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {`${swatches.length} couleur${swatches.length > 1 ? "s" : ""}`}{" "}
                    {swatches.length > 0 && <>{swatches.slice(0, 3)}</>}{" "}
                    {swatches.length > 3 && <> +{swatches.length - 3}</>}
                  </StyledTypography>
                )} */}
            {/* {swatches?.length <= 1 && product && productOptions && productOptionsCount > 1 && (
                  <StyledTypography variant="body4" component="p" pb={1}>
                    {`${productOptionsCount} options disponibles`}
                  </StyledTypography>
                )} */}
            {/* </StyledBox>
            )} */}
          </StyledBox>
        </StyledBox>

        {type === TYPE_CARD.INDISPENSABLE && (
          <StyledBox py={2} px={4} className="product-card-pack">
            {defaultProduct ? (
              "Votre produit"
            ) : (
              <FormGroup>
                <StyledFormControlLabel
                  control={
                    <StyledCheckbox
                      icon={<SvgCheckBoxOutlineBlankIcon className="blue-ultra-dark" />}
                      checkedIcon={<SvgCheckThick className="beige" />}
                      color="primary"
                      defaultChecked
                    />
                  }
                  disableTypography
                  label="Ajouter ce produit"
                  onChange={handlerChange}
                />
              </FormGroup>
            )}
          </StyledBox>
        )}
        <StyledBox
          sx={{
            position: "absolute",
            bottom: 16,
            left: 16,
            right: 16,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}>
          {swatches?.length > 1 && <ColorSwatches swatches={swatches} />}

          {isSurMesure && (
            <Chip
              label="SUR MESURE"
              sx={{
                width: "fit-content",
                borderRadius: "8px",
                border: "1px solid rgba(0, 0, 0, 0.10)",
                backgroundColor: "transparent",
                "& .MuiChip-label": {
                  padding: "4px 8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                },
              }}
            />
          )}
          {swatches?.length <= 1 && product && productOptions && productOptionsCount > 1 && (
            <StyledTypography variant="body4" component="p">
              {`${productOptionsCount} options disponibles`}
            </StyledTypography>
          )}
        </StyledBox>
      </StyledBox>
    </StyledProductCard>
  );
};

export default ProductCard;
